import { API_CONFIG, EXTERNAL_APIS } from '../config/env';
import { logger } from '../utils/logger';

type MediaHostsPayload = {
  tts?: unknown;
  asr?: unknown;
};

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * Parses a backend-provided host entry. Values without a scheme are treated as https.
 * Returns null for anything that is not a URL with a non-empty hostname.
 */
function parseHostUrl(value: unknown): URL | null {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const withScheme = SCHEME_RE.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    return url.hostname ? url : null;
  } catch {
    return null;
  }
}

/** Absolute `https://host` URL, e.g. `https://m4m-tts.gioitienganh.vn`. */
function toTtsHost(value: unknown): string | null {
  const url = parseHostUrl(value);
  return url ? `https://${url.host}` : null;
}

/** Scheme-less hostname, e.g. `m4m-asr.gioitienganh.vn` (callers build `wss://...`). */
function toAsrWsHost(value: unknown): string | null {
  const url = parseHostUrl(value);
  return url ? url.host : null;
}

function pickFirstHost(
  values: unknown,
  convert: (value: unknown) => string | null,
): string | null {
  if (!Array.isArray(values)) return null;
  for (const value of values) {
    const host = convert(value);
    if (host) return host;
  }
  return null;
}

const FALLBACK_TTS_HOST = toTtsHost(EXTERNAL_APIS.tts) ?? EXTERNAL_APIS.tts;
const FALLBACK_ASR_WS_HOST = toAsrWsHost(EXTERNAL_APIS.asrWsHost) ?? EXTERNAL_APIS.asrWsHost;

let ttsHost: string = FALLBACK_TTS_HOST;
let asrWsHost: string = FALLBACK_ASR_WS_HOST;
let initPromise: Promise<void> | null = null;

/**
 * The backend owns media host selection; the frontend only mirrors the first valid
 * entry it is given. Failures keep the last known good host (initially the
 * `EXTERNAL_APIS` bootstrap values) so the app keeps working.
 */
async function loadMediaHosts(): Promise<void> {
  try {
    const base = API_CONFIG.baseUrl.replace(/\/$/, '');
    const response = await fetch(`${base}/api/public/media-hosts`, {
      method: 'GET',
      credentials: 'omit',
    });
    if (!response.ok) {
      logger.warn(`[mediaHosts] media-hosts endpoint returned ${response.status}, keeping current hosts`, {
        ttsHost,
        asrWsHost,
      });
      return;
    }

    const payload = (await response.json()) as MediaHostsPayload;
    const nextTtsHost = pickFirstHost(payload?.tts, toTtsHost);
    const nextAsrWsHost = pickFirstHost(payload?.asr, toAsrWsHost);

    if (nextTtsHost) {
      ttsHost = nextTtsHost;
    }
    if (nextAsrWsHost) {
      asrWsHost = nextAsrWsHost;
    }
  } catch (error) {
    logger.warn('[mediaHosts] Failed to load media hosts, keeping current hosts', error);
  }
}

/** Loads media hosts once on startup. Memoized and never throws. */
export function initMediaHosts(): Promise<void> {
  if (!initPromise) {
    initPromise = loadMediaHosts();
  }
  return initPromise;
}

/** Forces a refetch so a backend-side host rotation is picked up without a FE deploy. */
export function refreshMediaHosts(): Promise<void> {
  initPromise = loadMediaHosts();
  return initPromise;
}

export function getTtsHost(): string {
  return ttsHost;
}

export function getAsrWsHost(): string {
  return asrWsHost;
}
